import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import axios from "axios";
import {
  MapPin,
  Clock,
  Heart,
  Wine,
  UtensilsCrossed,
  Music,
  Cake,
  Sparkles,
  Shirt,
  Church,
  X,
  Loader2,
  Play,
  Pause,
} from "lucide-react";
import { wedding, fmtCountdown } from "@/data/wedding";
import { Sprig } from "@/components/Ornaments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* -------- V3 LIGHT PALETTE - Warm paper / Editorial botanic --------
   Cream paper base with muted forest greens, deep charcoal ink, and
   a soft burnished gold. Photos are treated with a warm sepia overlay.
------------------------------------------------------------------- */
const C = {
  paper: "#F5EBD8",       // warm cream paper
  paperSoft: "#EFE1C7",   // slightly darker cream (alt bg)
  paperDeep: "#E8D5B2",   // deepest cream
  ink: "#1F2521",         // near-black with green undertone
  inkSoft: "#3E4A42",
  sage: "#6E876C",        // muted forest green
  sageDark: "#465B48",
  sageLight: "#B7C9B4",
  gold: "#A88338",        // burnished gold
  goldSoft: "#C6A566",
  rose: "#B87A6C",        // subtle rose accent
};

/* ------------------- YouTube helpers ------------------- */
let ytApi = null;
function loadYT() {
  if (ytApi) return ytApi;
  ytApi = new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve(window.YT);
    const s = document.createElement("script");
    s.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(s);
    window.onYouTubeIframeAPIReady = () => resolve(window.YT);
  });
  return ytApi;
}

/* ------------------- Decorative SVG ------------------- */
function LaurelBranch({ className = "", flip = false }) {
  return (
    <svg
      viewBox="0 0 120 180"
      className={className}
      aria-hidden="true"
      style={{ transform: flip ? "scaleX(-1)" : "none" }}
    >
      <g stroke="currentColor" strokeWidth="1" fill="none">
        <path d="M60 4 Q 58 90, 60 176" />
        {[20, 40, 60, 80, 100, 120, 140, 160].map((y) => (
          <g key={y}>
            <path
              d={`M60 ${y} q -22 -4 -34 -${y > 90 ? 12 : 18}`}
              fill="currentColor"
              fillOpacity="0.15"
            />
            <path
              d={`M60 ${y + 6} q 22 -4 34 -${y > 90 ? 12 : 18}`}
              fill="currentColor"
              fillOpacity="0.15"
            />
          </g>
        ))}
        <circle cx="60" cy="4" r="1.6" fill="currentColor" />
        <circle cx="60" cy="176" r="1.6" fill="currentColor" />
      </g>
    </svg>
  );
}

function DividerOrnament({ className = "" }) {
  return (
    <svg
      viewBox="0 0 240 24"
      className={className}
      aria-hidden="true"
    >
      <g stroke="currentColor" strokeWidth="1" fill="none" strokeLinecap="round">
        <path d="M4 12 H100" />
        <path d="M236 12 H140" />
        <path
          d="M120 4 C 108 12, 108 12, 120 20 C 132 12, 132 12, 120 4 Z"
          fill="currentColor"
          fillOpacity="0.12"
        />
        <circle cx="120" cy="12" r="1.6" fill="currentColor" />
        <circle cx="100" cy="12" r="1.2" fill="currentColor" />
        <circle cx="140" cy="12" r="1.2" fill="currentColor" />
      </g>
    </svg>
  );
}

function BotanicalCornerLight({ className = "", flip = false }) {
  return (
    <svg
      viewBox="0 0 200 200"
      className={className}
      aria-hidden="true"
      style={{ transform: flip ? "scaleX(-1)" : "none" }}
    >
      <g stroke="currentColor" strokeWidth="0.9" fill="none">
        <path d="M10 190 Q 40 120, 90 90 T 190 10" />
        <path d="M30 175 q 20 -18 42 -18" />
        <path
          d="M30 175 c 8 4 18 3 26 -2"
          fill="currentColor"
          fillOpacity="0.18"
        />
        <path d="M65 140 q 24 -12 44 -6" />
        <path
          d="M65 140 c 10 6 24 6 34 -1"
          fill="currentColor"
          fillOpacity="0.18"
        />
        <path d="M105 100 q 22 -8 42 4" />
        <path
          d="M105 100 c 12 6 26 6 36 1"
          fill="currentColor"
          fillOpacity="0.18"
        />
        <path d="M140 70 q 18 -6 34 6" />
        <circle cx="45" cy="176" r="2" fill="currentColor" />
        <circle cx="80" cy="140" r="1.6" fill="currentColor" />
        <circle cx="120" cy="102" r="1.6" fill="currentColor" />
        <circle cx="160" cy="70" r="1.4" fill="currentColor" />
      </g>
    </svg>
  );
}

/* Reusable typography */
const H2 = ({ children, italic = true, className = "" }) => (
  <h2
    className={className}
    style={{
      fontFamily: "'Playfair Display', serif",
      color: C.ink,
      fontSize: "clamp(1.9rem, 5.5vw, 2.6rem)",
      fontStyle: italic ? "italic" : "normal",
      fontWeight: 500,
      letterSpacing: "-0.01em",
    }}
  >
    {children}
  </h2>
);

const Overline = ({ children }) => (
  <p
    className="text-[11px] tracking-[0.4em] uppercase"
    style={{ color: C.sage, fontFamily: "'Montserrat', sans-serif" }}
  >
    {children}
  </p>
);

const Script = ({ children, size = "2rem", className = "" }) => (
  <span
    className={className}
    style={{
      fontFamily: "'Great Vibes', cursive",
      color: C.gold,
      fontSize: size,
      lineHeight: 1,
    }}
  >
    {children}
  </span>
);

/* Animated rolling digit for countdown */
function AnimatedDigit({ value }) {
  return (
    <div
      className="relative overflow-hidden inline-block"
      style={{ width: "1ch", height: "1.1em" }}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={value}
          initial={{ y: "100%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: "-100%", opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 flex items-center justify-center"
        >
          {value}
        </motion.span>
      </AnimatePresence>
    </div>
  );
}

function RollingNumber({ value }) {
  const digits = String(value).padStart(2, "0").split("");
  return (
    <div className="flex tabular-nums">
      {digits.map((d, i) => (
        <AnimatedDigit key={i} value={d} />
      ))}
    </div>
  );
}

/* ------------------------ SECTIONS ------------------------ */

function IntroGateV3({ open, onOpen }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 1 } }}
          className="fixed inset-0 z-[200] flex items-center justify-center"
          style={{ background: C.paper }}
          data-testid="v3-intro-gate"
        >
          {/* Photo backdrop with paper vignette */}
          <div className="absolute inset-0">
            <img
              src={wedding.hero}
              alt=""
              className="h-full w-full object-cover"
              style={{ filter: "sepia(0.35) contrast(0.9) brightness(1.02)" }}
            />
            <div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(180deg, ${C.paper}88 0%, ${C.paper}CC 40%, ${C.paper} 100%)`,
              }}
            />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.2 }}
            className="relative z-10 flex flex-col items-center justify-end min-h-[100svh] pb-16 pt-24 px-6 max-w-md mx-auto text-center"
          >
            <div className="flex-1" />
            <h1
              className="leading-[1.02]"
              style={{
                color: C.ink,
                fontFamily: "'Great Vibes', cursive",
                fontSize: "clamp(3rem, 11vw, 4.5rem)",
                fontWeight: 400,
                letterSpacing: "0.01em",
              }}
            >
              {wedding.bride}
              <span
                className="block leading-none my-1"
                style={{ color: C.gold, fontSize: "0.7em" }}
              >
                &
              </span>
              {wedding.groom}
            </h1>
            <p
              className="mt-6 text-[11px] tracking-[0.32em] uppercase"
              style={{ color: C.inkSoft, fontFamily: "'Montserrat', sans-serif" }}
            >
              {wedding.dateLabel.day} · {wedding.dateLabel.month} ·{" "}
              {wedding.dateLabel.year}
            </p>

            <div className="mt-10 mx-auto" style={{ color: C.sageDark }}>
              <DividerOrnament className="w-56 mx-auto" />
            </div>
            <Overline>Nuestra Boda</Overline>

            <motion.button
              onClick={onOpen}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.94 }}
              animate={{ scale: [1, 1.03, 1] }}
              transition={{ scale: { repeat: Infinity, duration: 2.4 } }}
              className="mt-6 inline-flex items-center gap-3 px-9 py-4 text-xs uppercase tracking-[0.32em]"
              style={{
                background: C.sageDark,
                color: C.paper,
                fontFamily: "'Montserrat', sans-serif",
              }}
              data-testid="v3-open-btn"
            >
              <Play size={13} fill="currentColor" />
              Iniciar
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function HeroV3() {
  return (
    <section
      className="relative min-h-[100svh] flex items-center justify-center overflow-hidden"
      style={{ background: C.paper }}
      data-testid="v3-hero"
    >
      <div className="absolute inset-0">
        <img
          src={wedding.hero}
          alt=""
          className="h-full w-full object-cover"
          style={{ filter: "sepia(0.25) contrast(0.95) brightness(1.02)" }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(180deg, ${C.paper}55 0%, ${C.paper}77 55%, ${C.paper} 100%)`,
          }}
        />
      </div>

      {/* Corner botanicals */}
      <LaurelBranch
        className="absolute top-6 left-2 w-16 opacity-70 md:opacity-80"
        style={{ color: C.sageDark }}
      />
      <LaurelBranch
        flip
        className="absolute top-6 right-2 w-16 opacity-70 md:opacity-80 -scale-y-100"
        style={{ color: C.sageDark, transform: "scaleX(-1)" }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.4, delay: 0.3 }}
        className="relative z-10 flex flex-col items-center justify-end min-h-[100svh] pb-24 pt-20 px-6 text-center"
      >
        <div className="flex-1" />
        <h1
          className="leading-[1.02]"
          style={{
            color: C.ink,
            fontFamily: "'Great Vibes', cursive",
            fontSize: "clamp(3.25rem, 12vw, 5rem)",
            fontWeight: 400,
            letterSpacing: "0.01em",
          }}
        >
          {wedding.bride}
          <span
            className="block leading-none my-1"
            style={{
              color: C.gold,
              fontSize: "0.7em",
            }}
          >
            &
          </span>
          {wedding.groom}
        </h1>
        <p
          className="mt-6 text-[11px] tracking-[0.4em] uppercase"
          style={{ color: C.inkSoft, fontFamily: "'Montserrat', sans-serif" }}
        >
          {wedding.dateLabel.weekday} · {wedding.dateLabel.day} ·{" "}
          {wedding.dateLabel.month} · {wedding.dateLabel.year}
        </p>
        <div className="mt-8" style={{ color: C.gold }}>
          <DividerOrnament className="w-56 mx-auto" />
        </div>
        <Overline>Save the date</Overline>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.6 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs tracking-[0.32em] uppercase"
        style={{ color: C.inkSoft }}
      >
        <span className="block animate-bounce">↓</span>
      </motion.div>
    </section>
  );
}

function PersonalMessageV3() {
  return (
    <section
      className="relative py-24 md:py-28 px-6 overflow-hidden"
      style={{ background: C.paperSoft }}
      data-testid="v3-personal-message"
    >
      <BotanicalCornerLight
        className="absolute -top-4 -left-4 w-44 md:w-60"
        style={{ color: C.sage }}
      />
      <BotanicalCornerLight
        flip
        className="absolute -bottom-4 -right-4 w-44 md:w-60 rotate-180"
        style={{ color: C.sage }}
      />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 1 }}
        className="relative max-w-xl mx-auto text-center"
      >
        <Overline>Con todo nuestro amor</Overline>
        <div className="mt-6 flex items-center justify-center gap-3" style={{ color: C.gold }}>
          <span className="h-px w-12" style={{ background: "currentColor" }} />
          <Heart size={14} />
          <span className="h-px w-12" style={{ background: "currentColor" }} />
        </div>
        <p
          className="mt-8 leading-[1.4]"
          style={{
            fontFamily: "'Dancing Script', cursive",
            color: C.ink,
            fontSize: "clamp(1.5rem, 5.5vw, 2rem)",
            fontWeight: 500,
          }}
        >
          Después de un hermoso camino juntos, hemos decidido dar el siguiente
          paso. Queremos compartir contigo este día tan especial y celebrar el
          inicio de nuestra nueva vida.
        </p>
        <p
          className="mt-8 text-[11px] tracking-[0.32em] uppercase"
          style={{ color: C.inkSoft, fontFamily: "'Montserrat', sans-serif" }}
        >
          — Dulce & José Manuel —
        </p>
      </motion.div>
    </section>
  );
}

function PadrinosV3() {
  const { bridesParents, groomsParents, godparents } = wedding.family;
  const groups = [bridesParents, godparents, groomsParents];
  return (
    <section
      className="relative py-24 md:py-28 px-6"
      style={{ background: C.paper }}
      data-testid="v3-padrinos"
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <Overline>Con la Bendición de</Overline>
          <H2 className="mt-4">Nuestras Familias</H2>
          <div className="mt-6" style={{ color: C.gold }}>
            <DividerOrnament className="w-56 mx-auto" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-14 md:gap-8">
          {groups.map((g, i) => (
            <motion.div
              key={g.label}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: i * 0.15 }}
              className="text-center"
              data-testid={`v3-padrinos-${i}`}
            >
              <p
                className="text-[10px] tracking-[0.4em] uppercase mb-4"
                style={{ color: C.sage, fontFamily: "'Montserrat', sans-serif" }}
              >
                {g.label}
              </p>
              <div className="flex items-center justify-center gap-3 mb-4" style={{ color: C.gold }}>
                <span className="h-px w-8" style={{ background: "currentColor" }} />
                <Sparkles size={12} />
                <span className="h-px w-8" style={{ background: "currentColor" }} />
              </div>
              <h3
                style={{
                  fontFamily: "'Playfair Display', serif",
                  color: C.ink,
                  fontSize: "1.5rem",
                  fontWeight: 500,
                }}
              >
                {g.names[0]}
                <span className="mx-2">
                  <Script size="1.9rem">&</Script>
                </span>
                {g.names[1]}
              </h3>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CountdownV3() {
  const [t, setT] = useState(fmtCountdown(wedding.date));
  useEffect(() => {
    const id = setInterval(() => setT(fmtCountdown(wedding.date)), 1000);
    return () => clearInterval(id);
  }, []);

  const items = [
    { v: t.days, l: "Días" },
    { v: t.hours, l: "Horas" },
    { v: t.minutes, l: "Minutos" },
    { v: t.seconds, l: "Segundos" },
  ];

  return (
    <section
      className="relative py-24 md:py-28 px-6"
      style={{ background: C.paperSoft }}
      data-testid="v3-countdown"
    >
      <div className="max-w-3xl mx-auto text-center">
        <Overline>Solo Faltan</Overline>
        <H2 className="mt-4">Para nuestro gran día</H2>
        <div className="mt-6" style={{ color: C.gold }}>
          <DividerOrnament className="w-56 mx-auto" />
        </div>

        <div className="mt-14 grid grid-cols-4 gap-3 sm:gap-5">
          {items.map((it, i) => (
            <motion.div
              key={it.l}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.6 }}
              className="flex flex-col items-center"
              data-testid={`v3-count-${it.l.toLowerCase()}`}
            >
              <div
                className="w-full py-6 sm:py-8"
                style={{
                  border: `1px solid ${C.gold}66`,
                  background: `${C.paper}`,
                }}
              >
                <div
                  className="flex justify-center"
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    color: C.ink,
                    fontSize: "clamp(1.75rem, 5vw, 3rem)",
                    lineHeight: 1,
                  }}
                >
                  <RollingNumber value={it.v} />
                </div>
              </div>
              <p
                className="mt-3 text-[10px] tracking-[0.4em] uppercase"
                style={{ color: C.sage, fontFamily: "'Montserrat', sans-serif" }}
              >
                {it.l}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GalleryV3() {
  const [active, setActive] = useState(null);
  return (
    <section
      className="relative py-24 md:py-28 px-6"
      style={{ background: C.paper }}
      data-testid="v3-gallery"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <Overline>Galería</Overline>
          <H2 className="mt-4">Momentos nuestros</H2>
          <div className="mt-6" style={{ color: C.gold }}>
            <DividerOrnament className="w-56 mx-auto" />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
          {wedding.gallery.map((src, idx) => (
            <motion.button
              key={src}
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: idx * 0.05 }}
              onClick={() => setActive(src)}
              className={`group relative overflow-hidden ${
                idx === 0 || idx === 3
                  ? "row-span-2 aspect-[3/4]"
                  : "aspect-square"
              }`}
              style={{ background: C.paperDeep }}
              data-testid={`v3-gallery-${idx}`}
            >
              <img
                src={src}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                style={{ filter: "sepia(0.12) contrast(0.98)" }}
              />
              <span
                className="pointer-events-none absolute inset-2 transition"
                style={{ border: `1px solid ${C.paper}00` }}
              />
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] backdrop-blur-sm flex items-center justify-center p-6"
            style={{ background: `${C.ink}E6` }}
            onClick={() => setActive(null)}
          >
            <button
              className="absolute top-6 right-6"
              style={{ color: C.paper }}
              onClick={() => setActive(null)}
              aria-label="Cerrar"
            >
              <X size={28} />
            </button>
            <motion.img
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              src={active}
              alt=""
              className="max-h-[88vh] max-w-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function EventCardV3({ data, isChurch, idx }) {
  const scriptAccent = isChurch ? "Templo" : "Salón";
  const venueSubtitle = isChurch
    ? "Rectoría San Pedro y San Pablo"
    : "Quinta el Rey";
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.9, delay: idx * 0.15 }}
      className="relative p-8 text-center"
      style={{
        border: `1px solid ${C.gold}55`,
        background: C.paper,
        color: C.ink,
      }}
      data-testid={`v3-event-${data.title.toLowerCase()}`}
    >
      <div
        className="mx-auto mb-5 flex items-center justify-center h-16 w-16 rounded-full"
        style={{ border: `1px solid ${C.sage}`, color: C.sageDark }}
      >
        {isChurch ? <Church size={26} /> : <Wine size={26} />}
      </div>
      <p
        className="text-[10px] tracking-[0.4em] uppercase"
        style={{ color: C.sage, fontFamily: "'Montserrat', sans-serif" }}
      >
        {isChurch ? "Celebración" : data.title}
      </p>
      {isChurch && (
        <p
          className="mt-1 text-[11px] italic"
          style={{
            color: C.inkSoft,
            fontFamily: "'Playfair Display', serif",
          }}
        >
          P. Emmanuel Bacilio Tobar
        </p>
      )}
      <div className="mt-3">
        <Script size="2.5rem">{scriptAccent}</Script>
      </div>
      <h3
        className="mt-1"
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "1.35rem",
          fontWeight: 500,
        }}
      >
        {venueSubtitle}
      </h3>

      <div className="mt-6 space-y-2 text-xs" style={{ color: C.inkSoft }}>
        {isChurch && (
          <div className="flex items-center justify-center gap-2">
            <Clock size={13} style={{ color: C.sage }} />
            <span className="tracking-[0.2em] uppercase">{data.time}</span>
          </div>
        )}
        <div className="flex items-center justify-center gap-2">
          <MapPin size={13} style={{ color: C.sage }} />
          <span>{data.address}</span>
        </div>
      </div>

      <a
        href={data.mapUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-7 inline-flex items-center gap-2 px-6 py-3 text-[11px] uppercase tracking-[0.32em] transition"
        style={{
          background: C.sageDark,
          color: C.paper,
          fontFamily: "'Montserrat', sans-serif",
        }}
      >
        <MapPin size={12} />
        Ver Ubicación
      </a>
    </motion.article>
  );
}

function EventsV3() {
  return (
    <section
      className="relative py-24 md:py-28 px-6"
      style={{ background: C.paperSoft }}
      data-testid="v3-events"
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <Overline>Eventos</Overline>
          <H2 className="mt-4">Te esperamos</H2>
          <div className="mt-6" style={{ color: C.gold }}>
            <DividerOrnament className="w-56 mx-auto" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <EventCardV3 data={wedding.ceremony} isChurch idx={0} />
          <EventCardV3 data={wedding.reception} idx={1} />
        </div>
      </div>
    </section>
  );
}

const PROGRAM_ICONS = {
  rings: Church,
  glass: Wine,
  plate: UtensilsCrossed,
  cake: Cake,
  music: Music,
  sparkle: Sparkles,
};

function ProgramV3() {
  return (
    <section
      className="relative py-24 md:py-28 px-6"
      style={{ background: C.paper }}
      data-testid="v3-program"
    >
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-14">
          <Overline>Programa del día</Overline>
          <H2 className="mt-4">Itinerario</H2>
          <div className="mt-6" style={{ color: C.gold }}>
            <DividerOrnament className="w-56 mx-auto" />
          </div>
        </div>

        <div className="relative">
          <div
            className="hidden md:block absolute left-0 right-0 top-[42px] h-px"
            style={{ background: `${C.gold}55` }}
          />
          <div className="grid grid-cols-2 md:grid-cols-6 gap-8 md:gap-4">
            {wedding.program.map((p, i) => {
              const Icon = PROGRAM_ICONS[p.icon] ?? Heart;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="flex flex-col items-center text-center"
                  data-testid={`v3-program-${i}`}
                >
                  <div
                    className="relative z-10 flex items-center justify-center h-[85px] w-[85px] rounded-full"
                    style={{
                      border: `1px solid ${C.sage}`,
                      background: C.paper,
                      color: C.sageDark,
                    }}
                  >
                    <Icon size={26} />
                  </div>
                  <div className="mt-4">
                    <Script size="1.65rem">{p.time}</Script>
                  </div>
                  <p
                    className="mt-1 text-[11px] tracking-[0.2em] uppercase"
                    style={{ color: C.inkSoft, fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {p.title}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function DressCodeV3() {
  return (
    <section
      className="relative py-24 md:py-28 px-6 overflow-hidden"
      style={{ background: C.paperSoft }}
      data-testid="v3-dresscode"
    >
      <Sprig
        className="absolute -left-4 top-10 w-20 opacity-45"
        style={{ color: C.sage }}
      />
      <Sprig
        className="absolute -right-4 bottom-10 w-20 opacity-45 rotate-180"
        style={{ color: C.sage }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.9 }}
        className="relative max-w-lg mx-auto text-center"
      >
        <Overline>Código de Vestimenta</Overline>
        <div
          className="mx-auto mt-6 flex items-center justify-center h-16 w-16 rounded-full"
          style={{ border: `1px solid ${C.gold}`, color: C.sageDark }}
        >
          <Shirt size={26} />
        </div>
        <p
          className="mt-6 italic"
          style={{
            fontFamily: "'Playfair Display', serif",
            color: C.ink,
            fontSize: "1.1rem",
            lineHeight: 1.7,
          }}
        >
          {wedding.dressCode.note}
        </p>
      </motion.div>
    </section>
  );
}

function RSVPV3() {
  const [name, setName] = useState("");
  const [attendance, setAttendance] = useState("yes");
  const [guests, setGuests] = useState(1);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Ingresa tu nombre.");
    setLoading(true);
    try {
      await axios.post(`${API}/rsvp`, {
        name: name.trim(),
        attendance,
        guests: Number(guests) || 1,
        message: message.trim(),
      });
      setDone(true);
      toast.success("¡Gracias! Confirmación recibida.");
    } catch (err) {
      toast.error("No se pudo guardar. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const fieldStyle = {
    borderColor: `${C.sage}`,
    color: C.ink,
    fontFamily: "'Montserrat', sans-serif",
  };

  return (
    <section
      className="relative py-24 md:py-28 px-6"
      style={{ background: C.paper }}
      data-testid="v3-rsvp"
    >
      <motion.div
        initial={{ opacity: 0, y: 22 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.9 }}
        className="max-w-lg mx-auto"
      >
        <div className="text-center mb-10">
          <Overline>Confirmación</Overline>
          <H2 className="mt-4">¿Nos acompañas?</H2>
          <p
            className="mt-5 text-sm"
            style={{ color: C.inkSoft, fontFamily: "'Montserrat', sans-serif" }}
          >
            Por favor confirma antes del 10 de octubre 2026.
          </p>
          <div className="mt-5" style={{ color: C.gold }}>
            <DividerOrnament className="w-56 mx-auto" />
          </div>
        </div>

        {done ? (
          <div
            className="text-center p-10"
            style={{
              border: `1px solid ${C.gold}66`,
              background: C.paperSoft,
              color: C.ink,
            }}
            data-testid="v3-rsvp-success"
          >
            <Heart className="mx-auto mb-4" style={{ color: C.gold }} />
            <h3
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "1.5rem",
              }}
            >
              ¡Gracias, {name.split(" ")[0]}!
            </h3>
            <p className="mt-3 text-sm" style={{ color: C.inkSoft }}>
              {attendance === "yes"
                ? "Estamos felices de celebrar contigo."
                : "Lamentamos que no puedas acompañarnos."}
            </p>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="p-8 space-y-7"
            style={{
              border: `1px solid ${C.gold}44`,
              background: C.paperSoft,
            }}
            data-testid="v3-rsvp-form"
          >
            <div>
              <label
                className="block text-[10px] tracking-[0.32em] uppercase mb-2"
                style={{ color: C.sage, fontFamily: "'Montserrat', sans-serif" }}
              >
                Nombre
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Tu nombre"
                className="w-full bg-transparent border-b py-2 focus:outline-none"
                style={fieldStyle}
                data-testid="v3-rsvp-name"
              />
            </div>

            <div>
              <label
                className="block text-[10px] tracking-[0.32em] uppercase mb-3"
                style={{ color: C.sage }}
              >
                Asistencia
              </label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { v: "yes", l: "Sí, asistiré" },
                  { v: "no", l: "No podré ir" },
                ].map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setAttendance(o.v)}
                    className="py-3 text-[10px] tracking-[0.32em] uppercase"
                    style={{
                      background: attendance === o.v ? C.sageDark : "transparent",
                      color: attendance === o.v ? C.paper : C.inkSoft,
                      border: `1px solid ${attendance === o.v ? C.sageDark : C.sage}`,
                      fontFamily: "'Montserrat', sans-serif",
                    }}
                    data-testid={`v3-rsvp-${o.v}`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </div>

            {attendance === "yes" && (
              <div>
                <label
                  className="block text-[10px] tracking-[0.32em] uppercase mb-2"
                  style={{ color: C.sage }}
                >
                  Asistentes
                </label>
                <input
                  value={guests}
                  onChange={(e) => setGuests(e.target.value)}
                  type="number"
                  min={1}
                  max={10}
                  className="w-full bg-transparent border-b py-2 focus:outline-none"
                  style={fieldStyle}
                />
              </div>
            )}

            <div>
              <label
                className="block text-[10px] tracking-[0.32em] uppercase mb-2"
                style={{ color: C.sage }}
              >
                Mensaje
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder="Tus buenos deseos…"
                className="w-full bg-transparent border-b py-2 focus:outline-none resize-none"
                style={fieldStyle}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 text-[11px] uppercase tracking-[0.32em] flex items-center justify-center gap-2 disabled:opacity-60 transition"
              style={{
                background: C.gold,
                color: C.paper,
                fontFamily: "'Montserrat', sans-serif",
              }}
              data-testid="v3-rsvp-submit"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              {loading ? "Enviando…" : "Confirmar Asistencia"}
            </button>
          </form>
        )}
      </motion.div>
    </section>
  );
}

function FooterV3() {
  return (
    <footer
      className="relative py-16 px-6 text-center"
      style={{
        background: C.paperSoft,
        borderTop: `1px solid ${C.gold}33`,
      }}
      data-testid="v3-footer"
    >
      <div className="mx-auto flex items-center justify-center gap-3 mb-4" style={{ color: C.gold }}>
        <span className="h-px w-16" style={{ background: "currentColor" }} />
        <Sparkles size={12} />
        <span className="h-px w-16" style={{ background: "currentColor" }} />
      </div>
      <p>
        <Script size="2.75rem">
          {wedding.bride} & {wedding.groom}
        </Script>
      </p>
      <p
        className="mt-3 text-[10px] tracking-[0.32em] uppercase"
        style={{ color: C.inkSoft, fontFamily: "'Montserrat', sans-serif" }}
      >
        {wedding.dateLabel.day} · {wedding.dateLabel.month} ·{" "}
        {wedding.dateLabel.year}
      </p>
      <p
        className="mt-8 text-[10px] tracking-[0.32em] uppercase italic"
        style={{ color: C.inkSoft, opacity: 0.55 }}
      >
        Hecho con amor
      </p>
    </footer>
  );
}

/* ---------- WhatsApp + Music (V3 style) ---------- */
function WhatsAppV3() {
  const text = encodeURIComponent(
    `Hola Dulce y José Manuel, quiero confirmar mi asistencia a su boda. ✨`
  );
  const url = `https://wa.me/${wedding.whatsappPhone}?text=${text}`;
  return (
    <motion.a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 1.4, type: "spring", stiffness: 200 }}
      whileHover={{ scale: 1.08 }}
      className="fixed bottom-6 right-6 z-50 flex items-center gap-3 pl-4 pr-5 py-3 rounded-full shadow-2xl"
      style={{
        background: "#25D366",
        color: "#fff",
        boxShadow: "0 12px 30px rgba(37,211,102,0.25)",
      }}
      data-testid="v3-whatsapp"
    >
      <svg viewBox="0 0 32 32" className="w-6 h-6 fill-white">
        <path d="M16 4C9.373 4 4 9.373 4 16c0 2.354.68 4.55 1.853 6.405L4 28l5.764-1.815A11.948 11.948 0 0 0 16 28c6.627 0 12-5.373 12-12S22.627 4 16 4z" />
      </svg>
      <span className="text-xs uppercase tracking-[0.32em] hidden sm:inline">
        Confirmar
      </span>
    </motion.a>
  );
}

const MusicPlayerV3 = React.forwardRef(function MusicPlayerV3(_p, ref) {
  const container = useRef(null);
  const player = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const videoId = wedding.musicVideoId;

  useEffect(() => {
    if (!videoId) return;
    let cancelled = false;
    loadYT().then((YT) => {
      if (cancelled || !container.current) return;
      player.current = new YT.Player(container.current, {
        videoId,
        playerVars: {
          autoplay: 1,
          controls: 0,
          loop: 1,
          playlist: videoId,
          playsinline: 1,
          modestbranding: 1,
          rel: 0,
        },
        events: {
          onReady: (e) => {
            e.target.mute();
            e.target.playVideo();
            setMuted(true);
            setPlaying(true);
          },
          onStateChange: (e) => {
            if (e.data === 1) setPlaying(true);
            if (e.data === 2) setPlaying(false);
          },
        },
      });
    });
    return () => {
      cancelled = true;
      try {
        player.current && player.current.destroy();
      } catch (e) {
        /* noop */
      }
    };
  }, [videoId]);

  React.useImperativeHandle(ref, () => ({
    startWithSound: () => {
      const p = player.current;
      if (!p) return;
      try {
        p.unMute();
        p.setVolume(55);
        p.playVideo();
        setMuted(false);
        setPlaying(true);
      } catch (e) {
        /* noop */
      }
    },
  }));

  const toggle = () => {
    const p = player.current;
    if (!p) return;
    if (playing && !muted) {
      p.pauseVideo();
      setPlaying(false);
    } else {
      p.unMute();
      p.setVolume(55);
      p.playVideo();
      setMuted(false);
      setPlaying(true);
    }
  };

  if (!videoId) return null;
  const active = playing && !muted;
  return (
    <>
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          bottom: -9999,
          left: -9999,
          width: 1,
          height: 1,
          overflow: "hidden",
        }}
      >
        <div ref={container} />
      </div>
      <motion.button
        onClick={toggle}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.6, type: "spring", stiffness: 200 }}
        whileHover={{ scale: 1.08 }}
        aria-label={active ? "Pausar" : "Reproducir"}
        className="fixed bottom-6 left-6 z-50 flex items-center justify-center h-12 w-12 rounded-full"
        style={{
          background: C.paper,
          border: `1px solid ${C.gold}`,
          color: C.sageDark,
          boxShadow: "0 10px 22px rgba(31, 37, 33, 0.22)",
        }}
        data-testid="v3-music-toggle"
      >
        {active ? (
          <Pause size={18} />
        ) : (
          <Music size={18} className="animate-pulse" />
        )}
      </motion.button>
    </>
  );
});

/* --------- PAGE --------- */
export default function InvitationV3() {
  const [opened, setOpened] = useState(false);
  const musicRef = useRef(null);

  const handleOpen = () => {
    setOpened(true);
    try {
      musicRef.current && musicRef.current.startWithSound();
    } catch (e) {
      /* noop */
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: C.paper, color: C.ink }}
      data-testid="v3-root"
    >
      <IntroGateV3 open={!opened} onOpen={handleOpen} />
      <HeroV3 />
      <PersonalMessageV3 />
      <PadrinosV3 />
      <CountdownV3 />
      <GalleryV3 />
      <EventsV3 />
      <ProgramV3 />
      <DressCodeV3 />
      <RSVPV3 />
      <FooterV3 />
      <WhatsAppV3 />
      <MusicPlayerV3 ref={musicRef} />
      <Toaster
        position="top-center"
        theme="light"
        toastOptions={{
          style: {
            background: C.paper,
            color: C.ink,
            border: `1px solid ${C.gold}88`,
          },
        }}
      />
    </div>
  );
}
